/**
 * Identite visuelle du client connecte.
 *
 * Le logiciel est revendu : rien de propre a une entreprise ne doit vivre dans
 * le code. Le nom, les couleurs et le logo viennent de la base (table
 * `organisations`) et sont charges apres la connexion, via GET /ma-marque.
 *
 * Avant la connexion, l'application ne PEUT PAS savoir a quel client
 * l'utilisateur appartient -- elle ne connait que son adresse e-mail une fois
 * saisie. L'ecran de connexion porte donc la marque RIDELLE, pour tout le
 * monde : afficher le logo d'un client sur la page d'accueil de tous les
 * autres serait exactement le defaut qu'on evite ici.
 *
 * DECISION DU 17/08/2026, et elle est commerciale autant que technique : la
 * connexion est en Ridelle pour tous ; une fois entre, le client retrouve SON
 * nom, SON logo et SES couleurs. L'ecran vu chaque matin par chaque
 * utilisateur porte le nom du produit -- un client qui recommande le logiciel
 * doit pouvoir le nommer.
 */
import { createContext, useContext } from "react";
import api from "./api";

/** La marque RIDELLE : avant connexion, et tant que celle du client n'est pas
 *  chargee. Ce n'est plus un repli neutre, c'est l'identite du produit.
 *
 *  Les couleurs sont celles du logo : un bleu marine profond et un bleu vif.
 *  Elles vivent ICI et nulle part ailleurs -- `appliquer()` les pose en
 *  variables CSS, et aucune feuille de style ne doit ecrire une couleur en
 *  dur, sinon le rethemage par client cesse de fonctionner. */
export const MARQUE_RIDELLE = {
  nom: "Ridelle",
  couleur_primaire: "#0B2149",   // bleu marine du logo
  couleur_secondaire: "#FFFFFF",
  couleur_accent: "#1266F1",     // bleu vif du logo
  logo_url: null,
};

/** Ancien nom, conserve pour ne rien casser ailleurs. */
export const MARQUE_NEUTRE = MARQUE_RIDELLE;

const CLE_CACHE = "marque";

/**
 * Contexte plutot que passage de proprietes : la marque sert dans Layout, que
 * chaque page monte elle-meme. La faire descendre par les proprietes
 * obligerait a la traverser dans les dix routes ET dans les huit pages, sans
 * qu'aucune ne s'en serve.
 */
export const ContexteMarque = createContext(MARQUE_NEUTRE);

export function useMarque() {
  return useContext(ContexteMarque);
}

/** Marque mise en cache au dernier chargement, pour eviter un clignotement
 *  aux couleurs par defaut a chaque rafraichissement de page.
 *
 *  LE LOGO TELEVERSE EN EST RETIRE, et c'est deliberé : il est servi par une
 *  route authentifiee, donc son adresse ne s'affiche PAS dans une balise
 *  <img> (voir chargerLogo). La garder en cache ferait apparaitre une image
 *  cassee a chaque rafraichissement, jusqu'a ce que le vrai logo arrive --
 *  soit exactement le clignotement que ce cache existe pour eviter.
 *
 *  Le client voit donc son NOM pendant la fraction de seconde ou le logo se
 *  charge. Un nom lisible vaut mieux qu'une icone d'image brisee. */
export function marqueEnCache() {
  try {
    const brut = localStorage.getItem(CLE_CACHE);
    if (!brut) return MARQUE_NEUTRE;
    const marque = { ...MARQUE_NEUTRE, ...JSON.parse(brut) };
    return marque.logo_televerse ? { ...marque, logo_url: null } : marque;
  } catch {
    return MARQUE_NEUTRE;
  }
}

/** Le texte lisible sur une couleur de fond : clair sur sombre, sombre sur clair.
 *
 *  LE MENU SUPPOSAIT UNE COULEUR PRINCIPALE SOMBRE, depuis toujours et sans
 *  que ce fut ecrit nulle part. Mesure le 17/08 avec une charte claire : le nom
 *  du client tombait a 1,10 pour 1 de contraste, les liens a 1,10, le nom de
 *  l'utilisateur a 1,06 -- il en faut 4,5. TOUT le menu s'effacait.
 *
 *  Le risque est devenu atteignable le jour ou l'editeur a pu changer les
 *  couleurs d'un client depuis l'ecran : rien n'empeche de choisir un beige.
 *
 *  On renvoie les composantes SEPAREES ("17, 24, 39") et non une couleur : le
 *  menu s'en sert dans des `rgba(var(--texte-menu-rvb), 0.55)`, pour garder ses
 *  transparences. Une couleur complete ne s'y glisserait pas.
 *
 *  Seuil a 0,5 de luminance perceptuelle : au-dela, le fond est clair et le
 *  texte doit devenir sombre. */
export function texteLisibleSur(couleurHex) {
  const hex = String(couleurHex || "").replace("#", "");
  if (hex.length !== 3 && hex.length !== 6) return "255, 255, 255";
  const plein = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(plein.slice(i, i + 2), 16) / 255);
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Gris tres sombre plutot que noir pur : moins dur a l'oeil, et c'est la
  // teinte deja utilisee ailleurs dans l'application.
  return luminance > 0.5 ? "17, 24, 39" : "255, 255, 255";
}

/** La couleur du client si elle se lit sur blanc, un gris tres fonce sinon.
 *
 *  Different de `texteLisibleSur` : ici le fond est FIXE (le blanc de la page)
 *  et c'est la couleur du texte qui varie. On ne bascule pas betement en noir
 *  des que la couleur est claire -- on la garde tant qu'elle atteint le seuil
 *  de lisibilite, pour que le rethemage du client reste visible.
 *
 *  Seuil a 4,5 pour 1, celui du texte courant (WCAG AA). */
export function couleurLisibleSurBlanc(couleurHex) {
  const SOMBRE = "17, 24, 39";
  const hex = String(couleurHex || "").replace("#", "");
  if (hex.length !== 3 && hex.length !== 6) return SOMBRE;
  const plein = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const composantes = [0, 2, 4].map((i) => parseInt(plein.slice(i, i + 2), 16));
  const lin = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = composantes.map(lin);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const contrasteSurBlanc = 1.05 / (luminance + 0.05);
  return contrasteSurBlanc >= 4.5 ? composantes.join(", ") : SOMBRE;
}

/** Applique les couleurs a la page entiere (variables CSS de :root). */
export function appliquerMarque(marque) {
  const racine = document.documentElement;
  racine.style.setProperty("--couleur-primaire", marque.couleur_primaire);
  racine.style.setProperty("--couleur-secondaire", marque.couleur_secondaire);
  racine.style.setProperty("--couleur-accent", marque.couleur_accent);
  racine.style.setProperty(
    "--texte-menu-rvb", texteLisibleSur(marque.couleur_primaire));
  // DEUX FONDS, DEUX VARIABLES. Le lien de l'ecran courant est peint avec la
  // couleur d'ACCENT, pas la principale : son texte doit contraster avec
  // l'accent. Leur donner la meme variable etait une erreur -- sur une charte
  // a fond sombre et accent clair, le lien actif serait redevenu illisible
  // pendant que le reste du menu allait bien.
  racine.style.setProperty(
    "--texte-accent-rvb", texteLisibleSur(marque.couleur_accent));
  // LE TEXTE DE LA PAGE, sur fond blanc. C'est `body` qui prenait la couleur
  // principale du client : sur une charte claire, le titre « Tableau de bord »
  // tombait a 1,10 pour 1 de contraste -- toute la page, pas seulement le menu.
  // On garde la couleur du client tant qu'elle se lit, et on bascule sur un
  // gris tres fonce sinon : le rethemage reste visible partout ou il est
  // lisible, et disparait la ou il ne l'est pas.
  racine.style.setProperty(
    "--texte-page-rvb", couleurLisibleSurBlanc(marque.couleur_primaire));
  document.title = `${marque.nom} — Gestion de flotte`;
}

/** Remet les couleurs neutres (deconnexion) : l'ecran de connexion ne doit
 *  pas garder les couleurs du dernier client passe sur ce navigateur. */
export function reinitialiserMarque() {
  localStorage.removeItem(CLE_CACHE);
  oublierLogo();
  appliquerMarque(MARQUE_NEUTRE);
}

/* ── Le logo televerse ────────────────────────────────────────────────────
 *
 * LE PIEGE DE CETTE FONCTIONNALITE, ET IL NE SE VOIT PAS DANS UN TEST SERVEUR.
 *
 * Depuis que le logo se televerse, il est servi par `GET /ma-marque/logo`,
 * une route AUTHENTIFIEE -- l'organisation vient du jeton, ce qui est
 * precisement ce qui empeche un client de demander le logo d'un autre.
 *
 * Mais une balise <img src="/ma-marque/logo"> n'envoie AUCUN en-tete
 * d'authentification : le navigateur ferait une requete nue, recevrait 401, et
 * afficherait une image cassee. Le serveur aurait raison, l'ecran serait faux.
 *
 * On charge donc les octets par `api` -- qui pose le jeton -- puis on en fait
 * une adresse locale (objectURL) que <img> sait afficher. C'est le meme motif
 * que les photos d'inspection (voir InspectionWizard).
 *
 * UN OBJECTURL SE REVOQUE. Sans cela, chaque rechargement de la marque (a
 * chaque connexion, et a chaque retour de consultation d'un client) en
 * laisserait un de plus en memoire, avec ses octets. On garde donc le dernier
 * et on l'oublie avant d'en creer un autre.
 */
let logoCourant = null;

function oublierLogo() {
  if (logoCourant) {
    URL.revokeObjectURL(logoCourant);
    logoCourant = null;
  }
}

async function chargerLogo() {
  const reponse = await api.get("/ma-marque/logo", { responseType: "blob" });
  oublierLogo();
  logoCourant = URL.createObjectURL(reponse.data);
  return logoCourant;
}

/** Charge la marque depuis le serveur et l'applique.
 *
 *  Les couleurs et le nom sont appliques TOUT DE SUITE, sans attendre le logo :
 *  ils viennent de la meme reponse, alors que le logo demande une seconde
 *  requete. Faire attendre l'habillage entier pour une image laisserait
 *  l'ecran aux couleurs par defaut plus longtemps que necessaire. */
export async function chargerMarque() {
  const { data } = await api.get("/ma-marque");
  const marque = { ...MARQUE_NEUTRE, ...data };

  // Le cache garde la marque SANS l'adresse locale du logo : un objectURL ne
  // survit pas au rechargement de la page, et un cache qui en porterait un
  // servirait une adresse morte au demarrage suivant.
  localStorage.setItem(CLE_CACHE, JSON.stringify(marque));
  appliquerMarque(marque);

  if (!marque.logo_televerse) {
    oublierLogo();
    return marque;
  }

  try {
    return { ...marque, logo_url: await chargerLogo() };
  } catch {
    // Un logo qui ne se charge pas ne doit pas priver le client de ses
    // couleurs ni de son nom : on retombe sur l'affichage sans logo, qui est
    // deja le cas normal des clients qui n'en ont pas.
    return { ...marque, logo_url: null };
  }
}
